import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  effect,
  inject,
  untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { RoleService } from '@app/shared/services/roles.service';
import { PlatformTerminologyConceptQuery } from '@app/core/APIs/app-apis';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { EzStandardStateComponent } from '@template-ui/components/ez-standard-state/ez-standard-state.component';
import type { TerminologyVocabularyCatalogItem } from './platform-terminology-admin.models';
import type {
  TerminologyConceptCatalog,
  TerminologyConceptDetail,
  TerminologyConceptGroup,
  TerminologyCatalogScope,
  TerminologyCatalogVariant,
} from './terminology-concept-catalog.models';
import { TerminologyConceptCatalogService } from './terminology-concept-catalog.service';

const PAGE_SIZE = 50;

/** Ephemeral navigation preferences only; every restored view still performs authorized reads. */
export interface TerminologyConceptCatalogViewState {
  authority: string;
  search: string;
  contextKey: string;
  variantKey: string;
  page: number;
  groupId: string | null;
  definitionId: number | null;
  scrollTop: number;
  contextOptions: TerminologyCatalogScope[];
  variantOptions: TerminologyCatalogVariant[];
}

@Component({
  selector: 'phms-terminology-concept-catalog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    UiButtonDirective,
    UiControlDirective,
    EzStandardStateComponent,
  ],
  templateUrl: './terminology-concept-catalog.component.html',
  styleUrl: './terminology-concept-catalog.component.scss',
})
export class TerminologyConceptCatalogComponent implements OnDestroy {
  private readonly api = inject(TerminologyConceptCatalogService);
  private readonly roles = inject(RoleService);
  readonly canRead = this.roles.hasAnyPermissionSignal('terminology.read');
  readonly canPreview = this.roles.hasAnyPermissionSignal(
    'terminology.preview',
  );
  readonly canAuthor = this.roles.hasAnyPermissionSignal('terminology.author');
  @Output() readonly editDefinition = new EventEmitter<number>();
  @Output() readonly createDefinition = new EventEmitter<void>();
  @Input() restoredState: TerminologyConceptCatalogViewState | null = null;
  @Output() readonly viewStateChange = new EventEmitter<TerminologyConceptCatalogViewState>();
  private groupList?: ElementRef<HTMLElement>;
  @ViewChild('groupList') private set renderedGroupList(value: ElementRef<HTMLElement> | undefined) {
    this.groupList = value;
    this.restoreListPosition();
  }

  catalog: TerminologyConceptCatalog | null = null;
  selectedGroup: TerminologyConceptGroup | null = null;
  selected: TerminologyConceptDetail | null = null;
  contextOptions: TerminologyCatalogScope[] = [];
  variantOptions: TerminologyCatalogVariant[] = [];
  contextKey = '';
  variantKey = '';
  search = '';
  page = 1;
  loading = false;
  detailLoading = false;
  accessLost = false;
  error = '';
  detailError = '';
  showFallback = false;
  private generation = 0;
  private detailGeneration = 0;
  private authority = '';
  private destroyed = false;
  private preferredGroupId: string | null = null;
  private preferredDefinitionId: number | null = null;
  private scrollTop = 0;
  private pendingListPosition: { request: number; scrollTop: number } | null = null;

  constructor() {
    effect(() => {
      const authority = this.currentAuthority();
      untracked(() => {
        if (authority === this.authority) return;
        this.clear(true);
        this.authority = authority;
        if (authority) {
          const restored = this.restoredState;
          if (restored?.authority === authority) {
            this.search = restored.search;
            this.contextKey = this.canPreview() ? restored.contextKey : '';
            this.variantKey = this.canPreview() ? restored.variantKey : '';
            this.contextOptions = restored.contextOptions.map(value => ({ ...value }));
            this.variantOptions = restored.variantOptions.map(value => ({ ...value }));
            this.page = Number.isSafeInteger(restored.page) && restored.page > 0 ? restored.page : 1;
            this.preferredGroupId = restored.groupId;
            this.preferredDefinitionId = restored.definitionId;
            this.scrollTop = Math.max(0, restored.scrollTop);
          }
          void this.load();
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.clear(true);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil((this.catalog?.totalCount ?? 0) / PAGE_SIZE));
  }
  get range(): string {
    const value = this.catalog;
    return !value || !value.totalCount
      ? '0 groups'
      : `${value.skip + 1}–${value.skip + value.items.length} of ${value.totalCount} groups`;
  }
  get canEditSelection(): boolean {
    return (
      !!this.selected &&
      this.selected.isCurrentContext &&
      this.selected.detail.definition.isCustomerEditable &&
      this.canAuthor() &&
      this.isCurrent() &&
      !this.loading &&
      !this.detailLoading &&
      !this.accessLost
    );
  }
  get resolutionLabel(): string {
    const effective = this.selected?.effective;
    if (!effective) return '';
    if (effective.usesFallback) return 'Declared product fallback';
    switch (effective.disposition.toLowerCase()) {
      case 'resolved':
        return 'Runtime-effective wording';
      case 'ambiguous':
        return 'Ambiguous resolution';
      case 'missing':
        return 'Missing resolution';
      case 'exempt':
        return 'Resolution exempted';
      default:
        return 'Wording unavailable';
    }
  }
  get resolved(): boolean {
    return (
      !!this.selected &&
      this.selected.effective.disposition.toLowerCase() === 'resolved'
    );
  }
  scopeKey(value: TerminologyCatalogScope): string {
    return JSON.stringify([
      value.tenantId ?? '',
      value.jurisdictionCode ?? '',
      value.programCode ?? '',
    ]);
  }
  scopeLabel(value: TerminologyCatalogScope): string {
    return (
      [
        value.tenantId,
        value.jurisdictionCode?.toUpperCase(),
        value.programCode?.toUpperCase(),
      ]
        .filter(Boolean)
        .join(' / ') || 'Platform'
    );
  }
  keyForVariant(value: TerminologyCatalogVariant): string {
    return JSON.stringify([
      value.localeCode,
      value.channelCode ?? '',
      value.audienceCode ?? '',
    ]);
  }
  variantLabel(value: TerminologyCatalogVariant): string {
    return [value.localeCode, value.channelCode, value.audienceCode]
      .filter(Boolean)
      .join(' / ');
  }
  memberLabel(value: TerminologyVocabularyCatalogItem): string {
    if (value.kindCode === 'concept') return 'Name';
    const code = value.grammaticalFormCode || value.kindCode;
    return ({ plural: 'Plural', inline: 'In a sentence', 'inline-plural': 'Plural in a sentence', uppercase: 'All capitals',
      phrase: 'Phrase', message: 'Message' } as Record<string, string>)[code]
      ?? this.fieldLabel(code).replace(/^./, value => value.toUpperCase());
  }
  matchLabel(group: TerminologyConceptGroup): string {
    return group.matches
      .map(
        (value) =>
          `${value.tokenCode} · ${this.fieldLabel(value.fieldCode)}: ${value.value}`,
      )
      .join('; ');
  }
  fieldLabel(code: string): string {
    return code.replace(/[_-]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
  }

  async load(): Promise<void> {
    if (!this.isCurrent()) {
      this.clear(true);
      return;
    }
    this.rememberSelection();
    if (this.groupList) this.scrollTop = this.groupList.nativeElement.scrollTop;
    const request = ++this.generation;
    this.pendingListPosition = null;
    ++this.detailGeneration;
    this.catalog = null;
    this.selected = null;
    this.selectedGroup = null;
    this.error = '';
    this.detailError = '';
    this.detailLoading = false;
    this.showFallback = false;
    this.loading = true;
    try {
      const catalog = await firstValueFrom(
        this.api.search({
          ...this.query(),
          search: this.search.trim() || undefined,
          skip: (this.page - 1) * PAGE_SIZE,
          take: PAGE_SIZE,
        }),
      );
      if (!this.validRequest(request)) return;
      this.catalog = catalog;
      this.contextOptions = catalog.availableContexts;
      this.variantOptions = catalog.supportedVariants;
      this.accessLost = false;
      this.loading = false;
      if (this.page > this.totalPages) {
        this.page = this.totalPages;
        await this.load();
        return;
      }
      if (catalog.items.length) {
        const group = catalog.items.find(value => value.groupId === this.preferredGroupId) ?? catalog.items[0];
        const member = group.members.some(value => value.definitionId === this.preferredDefinitionId)
          ? this.preferredDefinitionId : null;
        await this.selectGroup(group, member);
      }
      if (!this.validRequest(request)) return;
      this.publishViewState();
      this.pendingListPosition = { request, scrollTop: this.scrollTop };
      this.restoreListPosition();
    } catch (error) {
      if (!this.validRequest(request)) return;
      this.loading = false;
      this.setError(error, false);
    }
  }

  async applySearch(): Promise<void> {
    this.page = 1;
    this.resetSelection();
    await this.load();
  }
  async changeContext(value: string): Promise<void> {
    if (this.loading || !this.canPreview()) return;
    if (
      value &&
      !this.contextOptions.some((option) => this.scopeKey(option) === value)
    )
      return;
    this.contextKey = value;
    this.variantKey = '';
    this.page = 1;
    this.resetSelection();
    await this.load();
  }
  async changeVariant(value: string): Promise<void> {
    if (this.loading || !this.canPreview()) return;
    if (
      value &&
      !this.variantOptions.some(
        (option) => this.keyForVariant(option) === value,
      )
    )
      return;
    this.variantKey = value;
    this.page = 1;
    this.resetSelection();
    await this.load();
  }
  async goToPage(value: number): Promise<void> {
    if (
      this.loading ||
      value < 1 ||
      value > this.totalPages ||
      value === this.page
    )
      return;
    this.page = value;
    this.resetSelection();
    await this.load();
  }
  async selectGroup(group: TerminologyConceptGroup, retainedDefinitionId: number | null = null): Promise<void> {
    if (!this.catalog?.items.includes(group)) return;
    this.selectedGroup = group;
    await this.selectMember(
      retainedDefinitionId ?? group.matches[0]?.definitionId ?? group.representative.definitionId,
    );
  }
  async selectMember(definitionId: number): Promise<void> {
    if (
      !this.isCurrent() ||
      this.loading ||
      !this.selectedGroup?.members.some(
        (member) => member.definitionId === definitionId,
      )
    )
      return;
    const request = ++this.detailGeneration;
    const catalogRequest = this.generation;
    this.selected = null;
    this.detailError = '';
    this.showFallback = false;
    this.detailLoading = true;
    try {
      const selected = await firstValueFrom(
        this.api.definition(definitionId, this.query()),
      );
      if (
        !this.validRequest(catalogRequest) ||
        request !== this.detailGeneration
      )
        return;
      // A changed configuration/context must be refreshed before inspection continues.
      if (
        selected.configuration.configurationVersionId !==
          this.catalog?.configuration.configurationVersionId ||
        selected.configuration.versionCode !==
          this.catalog.configuration.versionCode ||
        selected.configuration.cacheRevisionToken !==
          this.catalog.configuration.cacheRevisionToken ||
        this.scopeKey(selected.context) !==
          this.scopeKey(this.catalog.context) ||
        this.keyForVariant(selected.context) !==
          this.keyForVariant(this.catalog.context)
      ) {
        this.detailLoading = false;
        this.detailError =
          'The authorized context changed. Refresh the catalog before inspecting this wording.';
        return;
      }
      this.selected = selected;
      this.detailLoading = false;
      this.publishViewState();
    } catch (error) {
      if (
        !this.validRequest(catalogRequest) ||
        request !== this.detailGeneration
      )
        return;
      this.detailLoading = false;
      this.setError(error, true);
    }
  }
  edit(): void {
    if (this.canEditSelection && this.selected) {
      this.publishViewState();
      this.editDefinition.emit(this.selected.detail.definition.definitionId);
    }
  }
  create(): void {
    if (this.canAuthor() && this.isCurrent() && !this.accessLost) {
      this.publishViewState();
      this.createDefinition.emit();
    }
  }
  captureScroll(event: Event): void {
    this.scrollTop = Math.max(0, (event.target as HTMLElement).scrollTop);
    this.publishViewState();
  }
  private restoreListPosition(): void {
    const pending = this.pendingListPosition;
    if (this.groupList && pending && this.validRequest(pending.request)) {
      this.groupList.nativeElement.scrollTop = pending.scrollTop;
      this.pendingListPosition = null;
    }
  }
  private rememberSelection(): void {
    if (this.selectedGroup) this.preferredGroupId = this.selectedGroup.groupId;
    if (this.selected) this.preferredDefinitionId = this.selected.detail.definition.definitionId;
  }
  private resetSelection(): void {
    this.selectedGroup = null;
    this.selected = null;
    this.preferredGroupId = null;
    this.preferredDefinitionId = null;
    this.scrollTop = 0;
    if (this.groupList) this.groupList.nativeElement.scrollTop = 0;
  }
  private publishViewState(): void {
    if (!this.isCurrent() || this.accessLost) return;
    this.rememberSelection();
    this.viewStateChange.emit({ authority: this.authority, search: this.search, contextKey: this.contextKey,
      variantKey: this.variantKey, page: this.page, groupId: this.preferredGroupId, definitionId: this.preferredDefinitionId,
      scrollTop: this.scrollTop, contextOptions: this.contextOptions.map(value => ({ ...value })),
      variantOptions: this.variantOptions.map(value => ({ ...value })) });
  }
  async checkAccess(): Promise<void> {
    this.clear(true);
    this.authority = this.currentAuthority();
    if (this.authority) await this.load();
  }
  retryDetail(): void {
    if (this.selectedGroup)
      void this.selectMember(
        this.selectedGroup.matches[0]?.definitionId ??
          this.selectedGroup.representative.definitionId,
      );
  }
  private query(): PlatformTerminologyConceptQuery {
    const scope = this.contextOptions.find(
      (value) => this.scopeKey(value) === this.contextKey,
    );
    const variant = this.variantOptions.find(
      (value) => this.keyForVariant(value) === this.variantKey,
    );
    return {
      ...(scope
        ? {
            tenantId: scope.tenantId ?? undefined,
            jurisdictionCode: scope.jurisdictionCode ?? undefined,
            programCode: scope.programCode ?? undefined,
          }
        : {}),
      ...(variant
        ? {
            localeCode: variant.localeCode,
            channelCode: variant.channelCode ?? undefined,
            audienceCode: variant.audienceCode ?? undefined,
          }
        : {}),
    };
  }
  private currentAuthority(): string {
    const profile = this.roles.accessProfile();
    return this.canRead() && profile?.actor.actorId
      ? JSON.stringify([
          profile.actor.actorId,
          profile.permissions,
          profile.scopes,
        ])
      : '';
  }
  private isCurrent(): boolean {
    return (
      !this.destroyed &&
      !!this.authority &&
      this.authority === this.currentAuthority()
    );
  }
  private validRequest(request: number): boolean {
    if (!this.isCurrent()) {
      this.clear(true);
      return false;
    }
    return request === this.generation;
  }
  private setError(error: unknown, detail: boolean): void {
    if (
      error instanceof HttpErrorResponse &&
      (error.status === 401 || error.status === 403)
    ) {
      this.clear(true);
      this.accessLost = true;
      return;
    }
    const message =
      'The catalog could not be refreshed. No current wording is displayed. Try again.';
    if (detail) this.detailError = message;
    else this.error = message;
  }
  private clear(resetContext: boolean): void {
    ++this.generation;
    ++this.detailGeneration;
    this.pendingListPosition = null;
    this.catalog = null;
    this.selected = null;
    this.selectedGroup = null;
    this.loading = false;
    this.detailLoading = false;
    this.error = '';
    this.detailError = '';
    this.showFallback = false;
    if (resetContext) {
      this.search = '';
      this.contextOptions = [];
      this.variantOptions = [];
      this.contextKey = '';
      this.variantKey = '';
      this.page = 1;
      this.preferredGroupId = null;
      this.preferredDefinitionId = null;
      this.scrollTop = 0;
    }
  }
}
